import './i18n/autoTranslate';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { US, GB, SA, FR } from 'country-flag-icons/react/3x2';

const Test = () => {
  const [selectedAccent, setSelectedAccent] = useState('en-US');
  const [selectedPersona, setSelectedPersona] = useState('us-professional');
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [testText, setTestText] = useState('Hello, welcome to our voice configuration system.');
  const [selectedVoice, setSelectedVoice] = useState(null);
  const voicesRef = useRef([]);

  const accents = [
    { value: 'en-US', label: 'American English', flag: <US title="United States" className="h-16 w-full rounded" /> },
    { value: 'en-GB', label: 'British English', flag: <GB title="United Kingdom" className="h-16 w-full rounded" /> },
    { value: 'ar-SA', label: 'اللغة العربية', flag: <SA title="Saudi Arabia" className="h-16 w-full rounded" /> },
    { value: 'fr-FR', label: 'French', flag: <FR title="France" className="h-16 w-full rounded" /> },
  ];

  const languagePersonas = {
    'en-US': [
      {
        id: 'us-professional',
        name: 'David Miller',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
        description: 'American professional male voice',
        gender: "male"
      },
      {
        id: 'us-friendly',
        name: 'Samantha Reed',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8dXRpbGlzYXRldXJ8ZW58MHx8MHx8fDA%3D',
        description: 'American friendly female voice',
        gender: "female"
      }
    ],
    'en-GB': [
      {
        id: 'gb-professional',
        name: 'Oliver Bennett',
        avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop&crop=face',
        description: 'British professional male voice',
        gender: "male"
      },
      {
        id: 'gb-friendly',
        name: 'Kate Morrison',
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
        description: 'British friendly female voice',
        gender: "female"
      }
    ],
    'ar-SA': [
      {
        id: 'arabic-male',
        name: 'محمد أحمد',
        avatar: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=150&h=150&fit=crop&crop=face',
        description: 'صوت عربي رجالي احترافي',
        gender: "male"
      },
      {
        id: 'arabic-female',
        name: 'سارة خالد',
        avatar: 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=150&h=150&fit=crop&crop=face',
        description: 'صوت عربي نسائي ودود',
        gender: "female"
      }
    ],
    'fr-FR': [
      {
        id: 'french-male',
        name: 'Thomas Bernard',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        description: 'Voix masculine française de haute qualité',
        gender: "male"
      },
      {
        id: 'french-female',
        name: 'Audrey Moreau',
        avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face',
        description: 'Voix féminine française premium',
        gender: "female"
      }
    ]
  };

  const currentPersonas = languagePersonas[selectedAccent] || languagePersonas['en-US'];

  const findBestVoice = useCallback((persona) => {
    const voices = voicesRef.current;
    if (!voices.length) return null;

    console.log('Finding voice for:', persona.name, persona.gender, selectedAccent);

    // Filter by language
    let filtered = voices.filter(v => {
      const lang = v.lang.toLowerCase();
      const target = selectedAccent.toLowerCase();
      return lang.startsWith(target.substring(0, 2)) || lang === target;
    });

    console.log('Language filtered:', filtered.length, 'voices');

    if (filtered.length === 0) {
      filtered = voices;
    }

    // Simple gender filter
    const genderFiltered = filtered.filter(v => {
      const name = v.name.toLowerCase();
      
      if (persona.gender === 'male') {
        // Exclude anything with "female" in the name
        if (name.includes('female')) return false;
        // Include male or neutral names
        return true;
      } else {
        // For female, prefer voices with "female" or known female names
        if (name.includes('female')) return true;
        const femaleNames = ['samantha', 'kate', 'victoria', 'audrey', 'amelie'];
        return femaleNames.some(n => name.includes(n));
      }
    });

    console.log('Gender filtered:', genderFiltered.length, 'voices');
    if (genderFiltered.length > 0) {
      console.log('Selected:', genderFiltered[0].name);
      return genderFiltered[0];
    }

    console.log('Fallback:', filtered[0]?.name);
    return filtered[0] || voices[0];
  }, [selectedAccent]);

  const speakText = useCallback((text) => {
    if (!voicesLoaded || isSpeaking || !text.trim()) return;

    const currentPersona = currentPersonas.find(p => p.id === selectedPersona);
    if (!currentPersona) return;

    const voice = findBestVoice(currentPersona);
    if (!voice) {
      alert('No voice available');
      return;
    }

    console.log('SPEAKING:', voice.name, voice.lang);
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setSelectedVoice(voice);
    };
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = (e) => {
      console.error('Error:', e);
      setIsSpeaking(false);
    };

    window.speechSynthesis.speak(utterance);
  }, [voicesLoaded, isSpeaking, currentPersonas, selectedPersona, findBestVoice]);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        voicesRef.current = voices;
        setVoicesLoaded(true);
        console.log('VOICES LOADED:', voices.length);
        console.log('All voices:', voices.map(v => v.name + ' (' + v.lang + ')').join('\n'));
      }
    };

    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    setTimeout(loadVoices, 100);
    setTimeout(loadVoices, 500);

    return () => {
      if (window.speechSynthesis.onvoiceschanged) {
        window.speechSynthesis.onvoiceschanged = null;
      }
      stopSpeaking();
    };
  }, [stopSpeaking]);

  const handleAccentChange = (newAccent) => {
    setSelectedAccent(newAccent);
    stopSpeaking();
    setSelectedVoice(null);
    
    const newPersonas = languagePersonas[newAccent];
    if (newPersonas && newPersonas.length > 0) {
      setSelectedPersona(newPersonas[0].id);
    }
    
    const textByLanguage = {
      'en-US': 'Hello, this is a test of the American English voice.',
      'en-GB': 'Hello, this is a test of the British English voice.',
      'ar-SA': 'مرحباً، هذا اختبار للصوت العربي.',
      'fr-FR': 'Bonjour, ceci est un test de la voix française.'
    };
    setTestText(textByLanguage[newAccent]);
  };

  const handlePersonaChange = (personaId) => {
    setSelectedPersona(personaId);
    setSelectedVoice(null);
    stopSpeaking();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Voice Configurator
          </h1>
          <p className="text-gray-600">
            Multi-language voice synthesis • Open console (F12) to see voice selection
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b">
              Select Language
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {accents.map((accent) => (
                <button
                  key={accent.value}
                  onClick={() => handleAccentChange(accent.value)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedAccent === accent.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-16 mb-3 overflow-hidden rounded-lg flex items-center justify-center">
                      {accent.flag}
                    </div>
                    <span className="font-semibold text-gray-800">
                      {accent.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b">
              Select Voice Persona
            </h2>
            <div className="space-y-4">
              {currentPersonas.map((persona) => (
                <button
                  key={persona.id}
                  onClick={() => handlePersonaChange(persona.id)}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    selectedPersona === persona.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={persona.avatar}
                      alt={persona.name}
                      className="w-16 h-16 rounded-full object-cover shadow"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-gray-800">
                            {persona.name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {persona.description}
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          persona.gender === 'male' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-pink-100 text-pink-800'
                        }`}>
                          {persona.gender}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b">
            Text to Speech
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter text to speak:
              </label>
              <textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900"
                rows="4"
                dir={selectedAccent === 'ar-SA' ? 'rtl' : 'ltr'}
              />
            </div>

            <div className="flex flex-wrap gap-4 items-center">
              <button
                onClick={() => speakText(testText)}
                disabled={!voicesLoaded || isSpeaking}
                className={`px-8 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  !voicesLoaded || isSpeaking
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                }`}
              >
                {isSpeaking ? 'Speaking...' : 'Test Voice'}
              </button>

              <button
                onClick={stopSpeaking}
                disabled={!isSpeaking}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  !isSpeaking
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 text-white shadow-md'
                }`}
              >
                Stop
              </button>

              <div className="ml-auto flex items-center gap-2">
                <div className={`h-3 w-3 rounded-full ${voicesLoaded ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <span className="text-sm text-gray-600">
                  {voicesLoaded ? `${voicesRef.current.length} voices` : 'Loading...'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Current Selection
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 mb-1">Language</p>
              <p className="font-bold text-gray-800">
                {accents.find(a => a.value === selectedAccent)?.label}
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-600 mb-1">Persona</p>
              <p className="font-bold text-gray-800">
                {currentPersonas.find(p => p.id === selectedPersona)?.name}
              </p>
            </div>
            <div className="bg-pink-50 p-4 rounded-lg">
              <p className="text-sm text-pink-600 mb-1">Gender</p>
              <p className="font-bold text-gray-800">
                {currentPersonas.find(p => p.id === selectedPersona)?.gender}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600 mb-1">Voice Used</p>
              <p className="font-bold text-gray-800 text-sm truncate" title={selectedVoice?.name}>
                {selectedVoice ? selectedVoice.name : 'Not yet tested'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Test;
